import type { CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '@/components/icons/Icon';
import { isLibraryIconRef, libraryIconSlug } from '@/lib/icon-library';
import {
  Accessibility,
  AlarmClock,
  Award,
  Ban,
  Bell,
  BookMarked,
  BookOpen,
  BookOpenCheck,
  Bot,
  Briefcase,
  Bug,
  Building2,
  Calendar,
  Camera,
  CircleHelp,
  ClipboardList,
  Clock,
  Cloud,
  Code2,
  Compass,
  CreditCard,
  Crown,
  Download,
  Eye,
  FileText,
  Flag,
  Flame,
  FolderKanban,
  FolderOpen,
  Gamepad2,
  Gift,
  Globe,
  Handshake,
  Headphones,
  Heart,
  Home,
  Image,
  Info,
  KeyRound,
  Languages,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link2,
  Lock,
  Mail,
  Map,
  MapPin,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Monitor,
  Music,
  Newspaper,
  Package,
  Palette,
  Pencil,
  Phone,
  Puzzle,
  Rocket,
  Scale,
  Search,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Tag,
  Target,
  Terminal,
  ThumbsUp,
  Ticket,
  Trash2,
  Trophy,
  Users,
  User,
  UserCheck,
  UserPlus,
  Video,
  Wallet,
  Wifi,
  Wrench,
  Zap,
} from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import {
  PowerCategoryNavIcon,
  PowerNavIcon,
  SmileNavIcon,
  UserConfigNavIcon,
  UserNavIcon,
} from '@/components/help-center/MaskedSvgIcon';


export const CMS_DEFAULT_ICON = 'BookOpenCheck';

export const CMS_ICON_OPTIONS: { value: string; label: string }[] = [
  { value: 'BookOpenCheck', label: 'Primeiros passos' },
  { value: 'BookOpen', label: 'Livro' },
  { value: 'BookMarked', label: 'Marcador' },
  { value: 'FileText', label: 'Documento' },
  { value: 'Newspaper', label: 'Notícias' },
  { value: 'ClipboardList', label: 'Lista' },
  { value: 'CircleHelp', label: 'Ajuda' },
  { value: 'LifeBuoy', label: 'Suporte' },
  { value: 'Info', label: 'Info' },
  { value: 'Lightbulb', label: 'Ideia' },
  { value: 'Compass', label: 'Bússola' },
  { value: 'Map', label: 'Mapa' },
  { value: 'MapPin', label: 'Local' },
  { value: 'Home', label: 'Início' },
  { value: 'LayoutGrid', label: 'Grelha' },
  { value: 'FolderOpen', label: 'Pasta' },
  { value: 'FolderKanban', label: 'Kanban' },
  { value: 'Search', label: 'Busca' },
  { value: 'User', label: 'Usuário' },
  { value: 'UserSvg', label: 'Conta (SVG)' },
  { value: 'UserConfig', label: 'Gestão de conta' },
  { value: 'Users', label: 'Equipe' },
  { value: 'UserCheck', label: 'Verificado' },
  { value: 'UserPlus', label: 'Convidar' },
  { value: 'Crown', label: 'Coroa' },
  { value: 'Shield', label: 'Escudo' },
  { value: 'ShieldCheck', label: 'Proteção' },
  { value: 'ShieldAlert', label: 'Alerta' },
  { value: 'Lock', label: 'Cadeado' },
  { value: 'KeyRound', label: 'Chave' },
  { value: 'Eye', label: 'Privacidade' },
  { value: 'Ban', label: 'Bloqueio' },
  { value: 'Flag', label: 'Denúncia' },
  { value: 'Scale', label: 'Regras' },
  { value: 'Handshake', label: 'Acordo' },
  { value: 'Zap', label: 'Raio' },
  { value: 'Power', label: 'Power' },
  { value: 'PowerSvg', label: 'Power (SVG)' },
  { value: 'Ace', label: 'Ace' },
  { value: 'Smile', label: 'Smiley' },
  { value: 'Sparkles', label: 'Brilho' },
  { value: 'Flame', label: 'Fogo' },
  { value: 'Rocket', label: 'Foguete' },
  { value: 'Star', label: 'Estrela' },
  { value: 'Award', label: 'Prémio' },
  { value: 'Trophy', label: 'Troféu' },
  { value: 'Target', label: 'Alvo' },
  { value: 'ThumbsUp', label: 'Gosto' },
  { value: 'Heart', label: 'Coração' },
  { value: 'Gift', label: 'Presente' },
  { value: 'Ticket', label: 'Ticket' },
  { value: 'Tag', label: 'Etiqueta' },
  { value: 'MessageSquare', label: 'Mensagem' },
  { value: 'MessageCircle', label: 'Chat' },
  { value: 'MessagesSquare', label: 'Conversas' },
  { value: 'Megaphone', label: 'Anúncio' },
  { value: 'Bell', label: 'Notificação' },
  { value: 'Mail', label: 'E-mail' },
  { value: 'Phone', label: 'Telefone' },
  { value: 'Headphones', label: 'Áudio' },
  { value: 'ShoppingBag', label: 'Saco' },
  { value: 'Store', label: 'Loja' },
  { value: 'Package', label: 'Pacote' },
  { value: 'CreditCard', label: 'Cartão' },
  { value: 'Wallet', label: 'Carteira' },
  { value: 'Wrench', label: 'Ferramentas' },
  { value: 'Settings', label: 'Definições' },
  { value: 'Puzzle', label: 'Puzzle' },
  { value: 'Bug', label: 'Bug' },
  { value: 'Code2', label: 'Código' },
  { value: 'Terminal', label: 'Terminal' },
  { value: 'Bot', label: 'Bot' },
  { value: 'Smartphone', label: 'Mobile' },
  { value: 'Monitor', label: 'Desktop' },
  { value: 'Wifi', label: 'Wi-Fi' },
  { value: 'Cloud', label: 'Nuvem' },
  { value: 'Download', label: 'Download' },
  { value: 'Share2', label: 'Partilhar' },
  { value: 'Link2', label: 'Link' },
  { value: 'Globe', label: 'Web' },
  { value: 'Languages', label: 'Idiomas' },
  { value: 'Video', label: 'Vídeo' },
  { value: 'Camera', label: 'Câmara' },
  { value: 'Image', label: 'Imagem' },
  { value: 'Music', label: 'Música' },
  { value: 'Gamepad2', label: 'Jogos' },
  { value: 'Palette', label: 'Cores' },
  { value: 'Pencil', label: 'Editar' },
  { value: 'Calendar', label: 'Calendário' },
  { value: 'Clock', label: 'Relógio' },
  { value: 'AlarmClock', label: 'Alarme' },
  { value: 'Briefcase', label: 'Trabalho' },
  { value: 'Building2', label: 'Empresa' },
  { value: 'Accessibility', label: 'Acessibilidade' },
  { value: 'Trash2', label: 'Lixo' },
];

const ICON_MAP: Record<string, LucideIcon> = {
  Accessibility,
  AlarmClock,
  Award,
  Ban,
  Bell,
  BookMarked,
  BookOpen,
  BookOpenCheck,
  Bot,
  Briefcase,
  Bug,
  Building2,
  Calendar,
  Camera,
  CircleHelp,
  ClipboardList,
  Clock,
  Cloud,
  Code2,
  Compass,
  CreditCard,
  Crown,
  Download,
  Eye,
  FileText,
  Flag,
  Flame,
  FolderKanban,
  FolderOpen,
  Gamepad2,
  Gift,
  Globe,
  Handshake,
  Headphones,
  Heart,
  Home,
  Image,
  Info,
  KeyRound,
  Languages,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  Link2,
  Lock,
  Mail,
  Map,
  MapPin,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Monitor,
  Music,
  Newspaper,
  Package,
  Palette,
  Pencil,
  Phone,
  Puzzle,
  Rocket,
  Scale,
  Search,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Tag,
  Target,
  Terminal,
  ThumbsUp,
  Ticket,
  Trash2,
  Trophy,
  Users,
  User,
  UserCheck,
  UserPlus,
  Video,
  Wallet,
  Wifi,
  Wrench,
  Zap,
  Ace: AceNavIcon,
  Power: PowerCategoryNavIcon,
  PowerSvg: PowerNavIcon,
  Smile: SmileNavIcon,
  UserConfig: UserConfigNavIcon,
  UserSvg: UserNavIcon,
};

export function getCmsIconComponent(name: string): LucideIcon {
  return ICON_MAP[name] ?? FolderOpen;
}

type CmsCategoryIconProps = {
  name: string;
  className?: string;
  style?: CSSProperties;
};

export function CmsCategoryIcon({ name, className, style }: CmsCategoryIconProps) {
  if (isLibraryIconRef(name)) {
    const slug = libraryIconSlug(name);
    if (slug) {
      return (
        <span className={className} style={style} aria-hidden>
          <Icon name={slug} className="h-full w-full" />
        </span>
      );
    }
  }
  const Glyph = getCmsIconComponent(name);
  return <Glyph className={className} style={style} aria-hidden />;
}
